"use client";

import React from "react";
import { FAQSection } from "@/components/FAQSection";
import { getTemplate, type TemplateData } from "@/lib/templates";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface TemplateSEOContentProps {
  template: TemplateData;
  locale: string;
  faqTitle: string;
  howToUseTitle: string;
  relatedTitle: string;
}

function localizeHref(href: string, locale: string) {
  if (href === "/") {
    return locale === "en" ? "/" : `/${locale}`;
  }
  if (href.startsWith("/") && locale !== "en") {
    return `/${locale}${href}`;
  }
  return href;
}

/**
 * Render inline links written as [label](/path) inside template copy.
 * Only links are supported; everything else is plain text.
 */
function renderInlineLinks(text: string, locale: string): React.ReactNode[] {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match) return part;
    const [, label, rawHref] = match;
    return (
      <a
        key={i}
        href={localizeHref(rawHref, locale)}
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {label}
      </a>
    );
  });
}

function TemplateLink({ slug, locale }: { slug: string; locale: string }) {
  const tpl = getTemplate(slug, locale);
  if (!tpl) return null;
  const href =
    locale === "en"
      ? `/templates/${slug}`
      : `/${locale}/templates/${slug}`;
  return (
    <Link
      href={href}
      className="group flex items-center gap-2 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
    >
      <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {tpl.relatedLabel ?? tpl.title}
      </span>
      <ArrowRight className="size-4 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ml-auto" />
    </Link>
  );
}

export function TemplateSEOContent({
  template,
  locale,
  faqTitle,
  howToUseTitle,
  relatedTitle,
}: TemplateSEOContentProps) {
  const {
    seoIntro,
    seoSections,
    howToSteps,
    faqItems,
    relatedTemplateSlugs,
  } = template;

  const relatedTemplates =
    relatedTemplateSlugs
      ?.map((slug) => getTemplate(slug, locale))
      .filter((t): t is TemplateData => t != null) ?? [];

  return (
    <>
      {/* SEO Intro */}
      {seoIntro && (
        <section className="container mx-auto px-4 pb-8 max-w-6xl">
          <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto text-center">
            {renderInlineLinks(seoIntro, locale)}
          </p>
        </section>
      )}

      {/* SEO Sections */}
      {seoSections?.map((section) => (
        <section
          key={section.id}
          className="container mx-auto px-4 pb-12 max-w-6xl"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            {section.heading}
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 p-8 md:p-12 shadow-sm">
            {section.paragraphs?.map((p, i) => (
              <p
                key={i}
                className="text-gray-600 dark:text-gray-300 mb-4 last:mb-0 leading-relaxed"
              >
                {renderInlineLinks(p, locale)}
              </p>
            ))}
            {section.bullets && section.bullets.length > 0 && (
              <ul className="space-y-3 mt-4">
                {section.bullets.map((bullet, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-gray-600 dark:text-gray-300"
                  >
                    <span className="mt-1.5 size-1.5 rounded-full bg-blue-500 dark:bg-blue-400 shrink-0" />
                    <span>{renderInlineLinks(bullet, locale)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {/* How-To Steps */}
      {howToSteps && howToSteps.length > 0 && (
        <section className="container mx-auto px-4 pb-12 max-w-6xl">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            {howToUseTitle}
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 p-8 md:p-12 shadow-sm">
            <ol className="space-y-4">
              {howToSteps.map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-gray-600 dark:text-gray-300"
                >
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-semibold text-white dark:bg-blue-400">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqItems && faqItems.length > 0 && (
        <FAQSection
          title={faqTitle}
          items={faqItems}
          bgColor="bg-transparent"
        />
      )}

      {/* Related Templates */}
      {relatedTemplates.length > 0 && (
        <section className="container mx-auto px-4 py-12 max-w-6xl">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
             {relatedTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {relatedTemplates.map((tpl) => (
              <React.Fragment key={tpl.slug}>
                <TemplateLink slug={tpl.slug} locale={locale} />
              </React.Fragment>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
